import { createSlice } from "@reduxjs/toolkit";
import produce, { original } from 'immer'
import { graphqlApi } from "../api";
import { createBuckeyeLinkSearchResults, filterBySubcategory, filterItemsByRole } from "../../../Search/util/functions";
import { STATUSES } from "../../../util/constants";
import { categories } from "../../../Search/util/categories";
import * as JsSearch from 'js-search'
import { formatQuickLinks } from "../functions";

const initialState = {
  status: STATUSES.IDLE,
  query: "",
  list: [],
  _list: [],
  _categorizedResults: [],
  categorizedResults: [],
  categories: [],
  cache: [],
  featuredResults: {},
  _quickLinks: [],
  quickLinks: [],
  affiliation: {
    _list: [],
    options: []
  }
};

const searchHelpfulLinks = ({ query = '', documents = [], customIndexes = [] }) => {
  const indexesToSearch = [...customIndexes, 'title', 'tags', 'snippet', 'category', 'subcategory']
  const index = documents?.[0]?.identifier ? "identifier" : "title"
  let search = new JsSearch.Search(index)
  indexesToSearch.map(o => search.addIndex(o))
  search.addDocuments(documents)

  return search.search(query)
}

const resortFilters = (filters = [], searchItems = []) => {
  const categoriesWithResults = searchItems.map(i => i.category)
  const filtersWithoutResults = filters.filter(f => !categoriesWithResults.includes(f.title))
  let filtersWithResults = filters.filter(f => categoriesWithResults.includes(f.title))

  if(filtersWithResults.length) {
    filtersWithResults = filtersWithResults.sort((a,b) => {
      const aIndex = categoriesWithResults.indexOf(a.title)
      const bIndex = categoriesWithResults.indexOf(b.title)
      return (aIndex > bIndex) ? 1 : -1
    })
  }  
  
  return filtersWithResults.concat(filtersWithoutResults)
}

const buckeyeLinkSlice = createSlice({
  name: "buckeyeLink",
  initialState,
  reducers: {
    search(state, action) {
      if(action?.payload?.clear) {
        state.categorizedResults = state._categorizedResults
      }
      const query = action?.payload?.query ?? ""
      if(!query || !state._list) {
        if(state._categorizedResults) {
          state.query = query ?? ""
          state.filters = state._filters
          state.categorizedResults = state._categorizedResults
        }
        return
      }
      state.query = query

      const match = state.cache.find(({ query: cacheQ }) => {
        return cacheQ === query
      })

      if(match?.query && match?.list?.length) {
        state.categorizedResults = match.categorizedResults
        state.query = match.query
        state.list = match.list
        state.filters = match.filters

        return
      }

      const documents = original(state._list)
      const newList = searchHelpfulLinks({
        query,
        documents
      })

      const data = {
        items: newList
      }
      state.list = newList

      const { categorizedResults } = createBuckeyeLinkSearchResults(data, {
        categories: state.categories,
        query
      });

      state.categorizedResults = categorizedResults
      const filters = resortFilters(state.filters, newList)
      state.filters = filters

      state.cache.push({
        query,
        list: newList,
        categorizedResults,
        filters
      })
    },
    updateReduxStore: (state, { payload }) => {
      state.status = STATUSES.SUCCESS
      const buckeyelink = payload?.data
      let data = {
        featured: buckeyelink?.featured,
        items: buckeyelink?.items
      }
      const options = { includeFilters: true }
      if(state.query) {
        options.query = state.query
      }
      state._list = data.items;

      const res =
        createBuckeyeLinkSearchResults(data, {
          includeFilters: true
        });

      const { items, categories, categorizedResults, filters } = res

      state.list = items;
      state._filters = filters
      state.filters = filters
      state.categories = categories;
      state.featuredResults = buckeyelink?.featured ?? {};
      state._categorizedResults = categorizedResults;
      state.categorizedResults = categorizedResults;
      state._quickLinks = payload?.data?._quickLinks ?? [];
      state.quickLinks = payload?.data?.quickLinks ?? [];
      const affiliations = payload?.data?.affiliations ?? []
      state.affiliation._list = affiliations
      state.affiliation.options = affiliations.map(({ role, label }) => {
        return {
          name: role,
          label,
          checked: false
        }
      })
    },
    updateAffiliationFilters: (state, { payload = {} }) => {
      const { name, checked } = payload

      if(state.affiliation.options && name) {
        const match = state.affiliation.options.find(filter => filter.name === name)
        match.checked = checked
      }
    },
    updateBuckeyeLinkData: (state, { payload = {} }) => {
      const { roles } = payload
      if(roles && state?._list?.length) {
        const stampOfCurrent = (state.roles ?? []).join()
        const stampOfNew = (roles ?? []).join()
        if(stampOfCurrent !== stampOfNew) {
          return modifyBuckeyeLinkResources(state, roles)
        }
      }
    }
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      graphqlApi.endpoints.getBuckeyeLinkData.matchPending,
      (state) => {
        state.status = STATUSES.LOADING
      },
      graphqlApi.endpoints.getBuckeyeLinkData.matchRejected,
      (state) => {
        state.status = STATUSES.ERROR
      }
    );
  },
});

const modifyBuckeyeLinkResources = (reduxState, roles) => {
  const countOfWellnessResourcesToRender = 3
  return produce(reduxState, (draftState) => {
    const wellnessState = draftState.featuredResults.wellness ?? []
    const newWellnessItems = wellnessState.map(resource => {
      let wellnessItems = filterBySubcategory(resource.id, draftState._list)?.items ?? []
      let remappedItems = filterLinksWithRoles(wellnessItems, roles)

      remappedItems = remappedItems.slice(0, countOfWellnessResourcesToRender)
      if(remappedItems?.length < countOfWellnessResourcesToRender) {
        const noRoleButRelated = wellnessItems.filter(({ roles }) => !roles?.length)
        remappedItems = remappedItems.concat(noRoleButRelated ?? [])
        remappedItems = remappedItems.slice(0, countOfWellnessResourcesToRender)
      }
      
      return {
        ...resource,
        items:remappedItems
      }
    })

    draftState.quickLinks = formatQuickLinks({ links: draftState?._quickLinks ?? [], roles })
    draftState.featuredResults.wellness = newWellnessItems
    draftState.roles = roles
  })
}

export const { search, updateReduxStore, updateBuckeyeLinkData, updateAffiliationFilters  } = buckeyeLinkSlice.actions;

export const selectBuckeyeLinkQuery = (state = {}) => {
  return state.buckeyeLink?.query ?? ""
};

export const selectCategories = (state = {}) => {
  return state.buckeyeLink?.categories ?? [];
};

export const selectCategorizedResults = (state = {} , options = {}) => {
  const { allowAffiliationFilter } = options ?? {}
  const affiliations = selectBuckeyeLinkAffiliationFilterOptions(state) ?? []

  const filters = state?.buckeyeLink?.filters ?? []
  let results = state.buckeyeLink?.categorizedResults
  const shouldTryToFilterOnAffil = affiliations && affiliations.find(o => o.checked)

  if(shouldTryToFilterOnAffil && allowAffiliationFilter) {
    results = results.map(({ result = {}, ...rest }) => {
      const newList = filterItemsByRole(result.items ?? [], affiliations)
      const newRes = {
        ...rest,
        totalResults: newList.length,
        result: {
          ...result,
          items: newList
        }
      }
      
      return newRes

    }).filter(({ totalResults }) => !!totalResults)
  }

  if(filters?.length) {
    results = results.map(({ ...res }) => {
      const match = filters.findIndex(({ ...o }) => {
        return o.id === res.id
      })
      res.sortPriority = match < 0 ? 0 : match
      return res
    })
    results = results.sort((a, b) => {
      return (a.sortPriority > b.sortPriority) ? 1 : -1
    })
  }
  

  return results
};

export const selectFilters = (state = {}, concat = true) => {
  let fils = state?.buckeyeLink?.filters ?? []
  if(concat) {
   fils = fils.concat(categories)
  }

  return fils
}

export const selectBuckeyeLinkFilterInformation = (state = {}, label) => {
  if(!label) {
    return {
      id: '',
      label: ''
    }
  }
  const match = selectBuckeyeLinkFilterMatch(state, label)
  return {
    id: match?.id || "",
    label: match?.title || ""
  }
}

export const selectBuckeyeLinkFilterMatch = (state = {}, label) => {
  const relevantFilters = state?.buckeyeLink?._filters || []
  if(!label) {
    return ''
  }
  const match = relevantFilters.find(({ title = '' }) => {
    const decoded = decodeURIComponent(label.replace(/\+/g, ' '))
    return (title === label) 
      || title.toUpperCase() === label.toUpperCase()
      || decoded === title
      || decoded.toUpperCase() === title.toUpperCase()
  })
  return match
}

export const selectBuckeyeLinkFilterId = (state = {}, label) => {
  if(!label) {
    return ''
  }
  const match = selectBuckeyeLinkFilterMatch(state, label)
  return match?.id || ""
}

export const selectBuckeyeLinkLinks = (state = {}) => {
  return state?.buckeyeLink?._list ?? []
}

export const calculateRoleMatch = (item = {}, roles) => {
  const { roles: linkRoles = [] } = item
  const matchesToRoles = (linkRoles ?? []).filter(role => roles?.length && roles.includes(role))
  return matchesToRoles?.length
}

export const filterLinksWithRoles = (items = [], roles) => {
  const anyLinksWithRoles = items.find(o => o?.roles?.length)

  let filtered = []
  if(anyLinksWithRoles) {
    filtered = items.filter((item) => {
      const { roles: linkRoles = [] } = item
      const matchesToRoles = (linkRoles ?? []).filter(role => roles?.length && roles.includes(role))
      return !!matchesToRoles?.length
    })
    filtered = filtered.sort((a, b) => {
      const aRoleMatches = calculateRoleMatch(a, roles)
      const bRoleMatches = calculateRoleMatch(b, roles)
      
      return bRoleMatches - aRoleMatches
    })
  }

  return filtered
}

export const selectFeaturedBuckeyeLinkData = (state,resourceType) => {
  const bl = state?.buckeyeLink
  const items = bl?.featuredResults?.[resourceType] ?? []
  
  return items
}

export const selectQuickLinks = (state) => { 
  return state?.buckeyeLink?.quickLinks ?? []
}

export const selectBuckeyeLinkAffiliationFilterOptions = (state) => { 
  const affilState = state?.buckeyeLink?.affiliation ?? {}
  const { options: storedOptions = [] } = affilState
  const options = (storedOptions ?? []).filter(opt => opt.name && opt.label)
  return Array.isArray(options) ? options : []
}

export const selectBuckeyeLinkAffiliationFilterOptionsComparator = (a, b) => {
  const concatOptionsByFields = (arr = []) => arr.map(o => o.name + o.label + (`checked-${o.checked}`)).join()
  const equality = concatOptionsByFields(a) === concatOptionsByFields(b)

  return equality
}

export const selectBuckeyeLinkAffiliationFilterState = (state) => { 
  let filterState = {}
  const options = selectBuckeyeLinkAffiliationFilterOptions(state)
  options.forEach((opt) => {
    filterState[opt.name] = Boolean(opt?.checked)
  })

  return filterState ?? {}
}

export const selectBuckeyeLinkAffiliationRenderabilityCondition = (state) => { 
  const options = selectBuckeyeLinkAffiliationFilterOptions(state)
  const affiliationState = selectBuckeyeLinkAffiliationFilterState(state)
  const canRender = options.length && Object.keys(affiliationState ?? {})?.length
  return !!canRender
}

export const selectBuckeyeLinkStatus = (state) => { 
  return state?.buckeyeLink?.status
}

const reducers = {
  getBuckeyeLink: buckeyeLinkSlice.reducer,
};
export default reducers;
